// ==================== PAYMENT METHODS PAGE ====================

import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  CreditCard, 
  Plus, 
  Trash2, 
  Star,
  RefreshCw,
  AlertCircle,
  CheckCircle,
  X,
  Lock,
  Shield
} from 'lucide-react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { Button, Tooltip } from '../../../components/ui';
import { subscriptionService, type PaymentMethod } from '../../../services/subscription.service';

const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY);

// Card brand icons - simplified version
const cardBrandIcons: Record<string, string> = {
  visa: '💳',
  mastercard: '💳',
  amex: '💳',
  discover: '💳',
  default: '💳',
};

// ==================== ADD PAYMENT FORM ====================
interface AddPaymentFormProps {
  onSuccess: () => void;
  onCancel: () => void;
}

const AddPaymentForm = ({ onSuccess, onCancel }: AddPaymentFormProps) => {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!stripe || !elements) return;
    
    setLoading(true);
    setError(null);

    try {
      const { error: submitError, setupIntent } = await stripe.confirmSetup({
        elements,
        redirect: 'if_required',
      });

      if (submitError) {
        setError(submitError.message || 'Failed to add payment method');
        return;
      }

      if (setupIntent && setupIntent.payment_method) {
        // Add payment method to our database
        const response = await subscriptionService.addPaymentMethod({
          paymentMethodId: setupIntent.payment_method as string,
          setAsDefault: false,
        });

        if (response.success) {
          onSuccess();
        } else {
          setError(response.message || 'Failed to save payment method');
        }
      }
    } catch {
      setError('An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-3">
      <PaymentElement 
        options={{
          layout: 'tabs',
        }}
      />
      
      {error && (
        <div className="p-2.5 bg-red-500/10 border border-red-500/30 rounded-lg">
          <p className="text-red-400 text-xs">{error}</p>
        </div>
      )}

      <div className="flex items-center gap-2 pt-1">
        <Button
          type="submit"
          variant="primary"
          size="sm"
          disabled={!stripe || loading}
          className="flex-1"
        >
          {loading ? (
            <><RefreshCw className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Adding...</>
          ) : (
            <><Plus className="w-3.5 h-3.5 mr-1.5" /> Add Payment Method</>
          )}
        </Button>
        <Button
          type="button"
          variant="secondary"
          size="sm"
          onClick={onCancel}
          disabled={loading}
        >
          Cancel
        </Button>
      </div>
    </form>
  );
};

// ==================== ADD PAYMENT WRAPPER ====================
interface AddPaymentWrapperProps {
  clientSecret: string;
  onSuccess: () => void;
  onCancel: () => void;
}

const AddPaymentWrapper = ({ clientSecret, onSuccess, onCancel }: AddPaymentWrapperProps) => {
  return (
    <Elements 
      stripe={stripePromise} 
      options={{ 
        clientSecret,
        appearance: {
          theme: 'night',
          variables: {
            colorPrimary: '#E68E37',
            colorBackground: '#121723',
            colorText: '#FFFFFF',
            colorDanger: '#EF4444',
            fontFamily: 'Inter, system-ui, sans-serif',
            borderRadius: '8px',
          },
        },
      }}
    >
      <AddPaymentForm onSuccess={onSuccess} onCancel={onCancel} />
    </Elements>
  );
};

// ==================== MAIN PAGE ====================
const PaymentMethodsPage = () => {
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [setupIntentSecret, setSetupIntentSecret] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);

  const fetchPaymentMethods = useCallback(async () => {
    try {
      setLoading(true);
      const response = await subscriptionService.getPaymentMethods();
      if (response.success) {
        setPaymentMethods(response.data || []);
      } else {
        setError(response.message || 'Failed to load payment methods');
      }
    } catch {
      setError('Failed to load payment methods');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPaymentMethods();
  }, [fetchPaymentMethods]);

  const handleAddPaymentMethod = async () => {
    try {
      setActionLoading('add');
      const response = await subscriptionService.createSetupIntent();
      if (response.success && response.data?.clientSecret) {
        setSetupIntentSecret(response.data.clientSecret);
        setShowAddForm(true);
      } else {
        setError(response.message || 'Failed to initialize payment form');
      }
    } catch {
      setError('Failed to initialize payment form');
    } finally {
      setActionLoading(null);
    }
  };

  const handleSetDefault = async (paymentMethodId: string) => {
    try {
      setActionLoading(paymentMethodId);
      const response = await subscriptionService.setDefaultPaymentMethod(paymentMethodId);
      if (response.success) {
        await fetchPaymentMethods();
      } else {
        setError(response.message || 'Failed to set default payment method');
      }
    } catch {
      setError('Failed to set default payment method');
    } finally {
      setActionLoading(null);
    }
  };

  const handleRemove = async (paymentMethodId: string) => {
    try {
      setActionLoading(paymentMethodId);
      const response = await subscriptionService.removePaymentMethod(paymentMethodId);
      if (response.success) {
        await fetchPaymentMethods();
        setDeleteConfirm(null);
      } else {
        setError(response.message || 'Failed to remove payment method');
      }
    } catch {
      setError('Failed to remove payment method');
    } finally {
      setActionLoading(null);
    }
  };

  const handleAddSuccess = () => {
    setShowAddForm(false);
    setSetupIntentSecret(null);
    fetchPaymentMethods();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-48">
        <div className="flex flex-col items-center gap-2">
          <RefreshCw className="w-6 h-6 text-orange-500 animate-spin" />
          <p className="text-sm text-(--color-muted-gray)">Loading payment methods...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-base font-heading font-semibold text-white">Payment Methods</h2>
          <p className="text-xs text-(--color-muted-gray) mt-0.5">Manage your payment methods for subscription billing</p>
        </div>
        {!showAddForm && (
          <Button 
            variant="primary" 
            size="sm"
            onClick={handleAddPaymentMethod}
            disabled={actionLoading === 'add'}
          >
            {actionLoading === 'add' ? (
              <><RefreshCw className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Loading...</>
            ) : (
              <><Plus className="w-3.5 h-3.5 mr-1.5" /> Add Payment Method</>
            )}
          </Button>
        )}
      </div>

      {/* Error Alert */}
      {error && (
        <motion.div 
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg flex items-center gap-2"
        >
          <AlertCircle className="w-4 h-4 text-red-400 shrink-0" />
          <p className="text-sm text-red-400 flex-1">{error}</p>
          <button onClick={() => setError(null)} className="text-red-400 hover:text-red-300">
            <X className="w-3.5 h-3.5" />
          </button>
        </motion.div>
      )}

      {/* Add Payment Method Form */}
      <AnimatePresence>
        {showAddForm && setupIntentSecret && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="bg-[#121723] rounded-xl border border-gray-800 p-4 overflow-hidden"
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-heading font-semibold text-white">Add New Payment Method</h3>
              <div className="flex items-center gap-1.5 text-(--color-muted-gray) text-xs">
                <Lock className="w-3.5 h-3.5" />
                <span>Secured by Stripe</span>
              </div>
            </div>
            <AddPaymentWrapper 
              clientSecret={setupIntentSecret}
              onSuccess={handleAddSuccess}
              onCancel={() => {
                setShowAddForm(false);
                setSetupIntentSecret(null);
              }}
            />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Payment Methods List */}
      <div className="bg-[#121723] rounded-xl border border-gray-800 overflow-hidden">
        {paymentMethods.length === 0 ? (
          <div className="p-8 text-center">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-gray-700/50 to-gray-800/50 flex items-center justify-center mx-auto mb-3">
              <CreditCard className="w-6 h-6 text-gray-500" />
            </div>
            <h3 className="text-sm font-heading font-semibold text-white mb-1.5">No Payment Methods</h3>
            <p className="text-xs text-(--color-muted-gray) mb-4 max-w-xs mx-auto">
              Add a payment method to manage your subscription billing.
            </p>
            <Button variant="primary" size="sm" onClick={handleAddPaymentMethod}>
              <Plus className="w-3.5 h-3.5 mr-1.5" /> Add Your First Card
            </Button>
          </div>
        ) : (
          <div className="divide-y divide-gray-800">
            {paymentMethods.map((pm) => (
              <div key={pm.id} className="p-3 flex items-center gap-3 hover:bg-[#1A2332]/50 transition-colors">
                {/* Card Icon */}
                <div className="w-10 h-7 bg-gradient-to-br from-gray-700 to-gray-800 rounded flex items-center justify-center">
                  <span className="text-lg">{cardBrandIcons[pm.brand?.toLowerCase() || 'default']}</span>
                </div>

                {/* Card Details */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5">
                    <p className="text-sm text-white font-medium capitalize">
                      {pm.brand || 'Card'} •••• {pm.last4}
                    </p>
                    {pm.isDefault && (
                      <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 bg-emerald-500/15 text-emerald-400 text-[10px] font-medium rounded-full">
                        <CheckCircle className="w-2.5 h-2.5" />
                        Default
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-(--color-muted-gray)">
                    Expires {pm.expiryMonth}/{pm.expiryYear}
                  </p>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-1.5">
                  {!pm.isDefault && (
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => handleSetDefault(pm.id)}
                      disabled={actionLoading === pm.id}
                    >
                      {actionLoading === pm.id ? (
                        <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                      ) : (
                        <><Star className="w-3.5 h-3.5 mr-1" /> Set Default</>
                      )}
                    </Button>
                  )}
                  
                  {/* Delete button - disabled for default payment method */}
                  {pm.isDefault ? (
                    <Tooltip content="Set another card as default first" position="top">
                      <div className="p-1.5 text-gray-600 cursor-not-allowed rounded">
                        <Trash2 className="w-3.5 h-3.5" />
                      </div>
                    </Tooltip>
                  ) : deleteConfirm === pm.id ? (
                    <div className="flex items-center gap-1.5">
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={() => handleRemove(pm.id)}
                        disabled={actionLoading === pm.id}
                        className="bg-red-500! hover:bg-red-600!"
                      >
                        {actionLoading === pm.id ? (
                          <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                        ) : (
                          'Confirm'
                        )}
                      </Button>
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => setDeleteConfirm(null)}
                      >
                        Cancel
                      </Button>
                    </div>
                  ) : (
                    <button
                      onClick={() => setDeleteConfirm(pm.id)}
                      className="p-1.5 text-gray-400 hover:text-red-400 hover:bg-red-500/10 rounded transition-colors"
                      title="Remove payment method"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Security Note */}
      <div className="flex items-start gap-2.5 p-3 bg-[#1A2332]/50 rounded-lg border border-gray-800">
        <Shield className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" />
        <div>
          <p className="text-xs text-gray-300 font-medium">Your payment information is secure</p>
          <p className="text-[11px] text-(--color-muted-gray) mt-0.5">
            All payment information is encrypted and securely processed by Stripe. 
            We never store your full card number on our servers.
          </p>
        </div>
      </div>
    </div>
  );
};

export default PaymentMethodsPage;

